import { Grid, TextField, Button, Typography, Box } from "@mui/material";
import { DbUser, ClassExtended } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import { makeStyles } from "@mui/styles";
import { Role, SubRoles } from "../../../../enums";
import { formatDate, generateGtmEvent } from "../../../../Helpers";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const useStyles = makeStyles({
  textField: {
    "& .MuiFormLabel-root": {
      fontWeight: 500,
      fontSize: 14,
      color: "#747474",
      textTransform: "uppercase",
      letterSpacing: 0.7,
    },
    "& .MuiInputBase-root": {
      color: "#484848",
      borderBottom: "none !important",
      fontSize: 16,
      fontWeight: 400,
      paddingBottom: "1rem",
      "& .Mui-disabled": {
        "-webkit-text-fill-color": "unset",
      },
    },
  },
  borderBottom: {
    width: "100%",
    borderBottom: "1px solid #E8E8E8",
  },
});

export type ViewClassProps = {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  currentUser: DbUser;
  classe?: ClassExtended;
};

export function ViewClass(props: ViewClassProps) {
  const classes = useStyles();
  const display =
    props.currentUser?.role !== Role.teacher &&
    !props.currentUser?.user_subrole?.some(
      (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
    );

  const displaySubRole = props.currentUser?.role === Role.super;

  if (props.classe) {
    const title = `${props.classe.course_name} ${props.classe.period_block}  ${
      props.classe.start_date && formatDate(new Date(props.classe.start_date))
    }`;

    const fieldProps = {
      fullWidth: true,
      disabled: true,
      className: classes.textField,
      InputProps: {
        disableUnderline: true,
      },
    };

    const divider = <div className={classes.borderBottom} />;
    const HandleDataLayer = (
      event: string,
      action: string,
      name: string,
      type: string,
      region: string,
      section: string,
      text: string
    ) => {
      generateGtmEvent({ event, action, name, type, region, section, text });
    };

    return (
      <UserManagementModal
        hideSubmitButton={true}
        title={title}
        titleElem={
          <>
            {title}
            {display && (
              <Button
                variant="outlined"
                size={"small"}
                disableElevation
                sx={{
                  marginLeft: "1em",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "primary.light",
                  },
                }}
                onClick={() => {
                  HandleDataLayer(
                    "link",
                    "click",
                    "onclick",
                    "internal link",
                    "modal",
                    title,
                    "edit"
                  );
                  props.onEdit();
                }}>
                Edit
              </Button>
            )}
          </>
        }
        open={props.open}
        onClose={props.onClose}>
        <Grid container spacing={6}>
          <Grid item xs={6} order={1}>
            <TextField
              label="School"
              value={props.classe.school_name?.length ? props.classe.school_name : ""}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={2}>
            <TextField
              label="Teacher"
              value={props.classe.teacher_name ? props.classe.teacher_name : ""}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={3}>
            <TextField
              label="Course"
              value={props.classe.course_name}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={4}>
            <TextField
              label="Period/block"
              value={props.classe.period_block}
              variant="standard"
              {...fieldProps}
            />
            {divider}
          </Grid>
          <Grid item xs={6} order={5}>
            <TextField
              label="Start Date"
              value={
                props.classe.start_date && formatDate(new Date(props.classe.start_date))
              }
              variant="standard"
              {...fieldProps}
            />
          </Grid>
          <Grid item xs={6} order={6}>
            <TextField
              label="End Date"
              value={props.classe.end_date && formatDate(new Date(props.classe.end_date))}
              variant="standard"
              {...fieldProps}
            />
          </Grid>
          {displaySubRole && (
            <Grid item xs={6} order={7}>
              <Typography
                sx={{
                  color: "#747474",
                  fontSize: 10.5,
                  fontWeight: 500,
                  letterSpacing: 0.7,
                }}>
                USER INTERFACE TESTING
              </Typography>
              <Box sx={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                {props.classe.is_tester ? <CheckIcon /> : <CloseIcon />}
                <Typography>
                  {props.classe.is_tester
                    ? "Marked as a test class"
                    : "Not marked as a test class"}
                </Typography>
              </Box>
            </Grid>
          )}
          {displaySubRole && (
            <Grid item xs={6} order={8}>
              <TextField
                label="Class ID"
                value={`${props.classe.id}`}
                variant="standard"
                {...fieldProps}
              />
            </Grid>
          )}
        </Grid>
      </UserManagementModal>
    );
  } else {
    return <></>;
  }
}
